import { CourseStatsBody } from "JS/Models";
import {
    FBCourseResponse,
    FBCourseStats,
    FBFunctionStepStats,
    FBStepStats,
    StepActions,
} from "JS/Models/Firebase/Courses";
import {
    CourseFirebaseService,
    IAttendanceDetails,
    courseFirebaseService,
} from "JS/Services/Firebase";
import { useSnackbar } from "JS/Helpers/Snackbar";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    useWriteOfflineCourseStats,
    useWriteOfflineStepStats,
} from "../DownloadCourses";
import { messaging } from "JS/Helpers/UserMessaging";
import { bootstrapFirebaseService } from "JS/Helpers";

export const useWriteCourseStatsFBFunc = () => {
    const [loading, setLoading] = useState(false);
    const [courseStats, setCourseStats] = useState<FBCourseResponse>(null);
    const { writeOfflineFBCourseStats } = useWriteOfflineCourseStats();

    const { enqueueSnackbar } = useSnackbar();
    const serviceRef = useRef<CourseFirebaseService | null>(null);

    useEffect(() => {
        const initService = async () => {
            const service = new CourseFirebaseService();
            await service.init();
            serviceRef.current = service;
        };
        initService();
    }, []);

    const writeFBCourseStats = useCallback(
        (courseStats: CourseStatsBody, skipOfflineStats?: boolean) => {
            if (!serviceRef.current) {
                console.warn("Firebase service not initialized yet");
                return Promise.resolve(null as FBCourseResponse);
            }
            setLoading(true);
            return serviceRef.current
                .writeCourseStatsOnFB(courseStats)
                .then((updatedStats) => {
                    setCourseStats(updatedStats);
                    !skipOfflineStats &&
                        writeOfflineFBCourseStats(
                            courseStats.skuId,
                            updatedStats.stats,
                        );
                    return updatedStats;
                })
                .catch((err) => {
                    enqueueSnackbar(messaging.common.error, {
                        variant: "error",
                    });

                    return null as FBCourseResponse;
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [],
    );

    return {
        writeFBCourseStats,
        courseStats,
        loading,
    };
};

export const useWriteOfflineCourseStatsFBFunc = () => {
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const serviceRef = useRef<CourseFirebaseService | null>(null);

    useEffect(() => {
        const initService = async () => {
            const service = new CourseFirebaseService();
            await service.init();
            serviceRef.current = service;
        };
        initService();
    }, []);

    const writeOffineCourseStatsFB = useCallback(
        (courseSku: string, stats: FBCourseStats) => {
            if (!serviceRef.current) {
                console.warn("Firebase service not initialized yet");
                return Promise.resolve(null as FBCourseStats);
            }
            setLoading(true);

            return serviceRef.current
                .writeOfflineCourseStatsOnFB(courseSku, stats)
                .then((updatedStats) => updatedStats)
                .catch((err) => {
                    enqueueSnackbar(messaging.common.error, {
                        variant: "error",
                    });

                    return null as FBCourseStats;
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [],
    );

    return {
        writeOffineCourseStatsFB,
        loading,
    };
};

export const useWriteStepStats = () => {
    const [loading, setLoading] = useState(false);
    const [stepStats, setStepStats] = useState<FBStepStats>(null);
    const [serviceReady, setServiceReady] = useState(false);
    const [serviceError, setServiceError] = useState(false);
    const { writeOfflineFBStepStats } = useWriteOfflineStepStats();
    const { enqueueSnackbar } = useSnackbar();
    const serviceRef = useRef<CourseFirebaseService | null>(null);

    useEffect(() => {
        const initService = async () => {
            try {
                const service = new CourseFirebaseService();
                await service.init();
                serviceRef.current = service;
                setServiceReady(true);
            } catch (err) {
                setServiceError(true);
            }
        };
        initService();
    }, []);

    const writeFBStepStats = useCallback(
        (
            courseSkuId: string,
            stepSkuId: string,
            action: StepActions,
            stats: Partial<FBFunctionStepStats>,
        ) => {
            if (!serviceRef.current) {
                console.warn("Firebase service not initialized yet");
                return Promise.resolve(null as FBStepStats);
            }
            setLoading(true);

            return serviceRef.current
                .writeStepStats(courseSkuId, stepSkuId, action, stats)
                .then((statsData) => {
                    writeOfflineFBStepStats(
                        courseSkuId,
                        stepSkuId,
                        statsData.data.stats,
                    );
                    setStepStats(statsData.data.stats);
                    return statsData.data.stats;
                })
                .catch((err) => {
                    enqueueSnackbar(err.message, {
                        variant: "error",
                    });
                    return null as FBStepStats;
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [enqueueSnackbar],
    );

    return {
        writeFBStepStats,
        stepStats,
        loading,
        serviceReady,
        serviceError,
    };
};

export const useWriteEventAttendance = () => {
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const writeEventAttendance = useCallback(
        (actionDetails: IAttendanceDetails) => {
            setLoading(true);
            bootstrapFirebaseService();
            return courseFirebaseService
                .markAttendance(actionDetails)
                .then((res) => {
                    if (res) {
                        enqueueSnackbar(actionDetails?.successMessage, {
                            variant: "success",
                        });
                    } else {
                        enqueueSnackbar(actionDetails?.failureMessage, {
                            variant: "error",
                        });
                    }
                    return res;
                })
                .catch(() => {
                    enqueueSnackbar(actionDetails?.failureMessage, {
                        variant: "error",
                    });
                    return null;
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [],
    );

    return {
        writeEventAttendance,
        loading,
    };
};
